import { IGiftCardSpace, IGroup, IGroupItems, IGiftCardSpaceSection } from 'gift-cards/types';
import { transformSpaces } from 'gift-cards/utils/transformSpaces';
import { initialSpaceItemsPlacement } from 'gift-cards/enums/constants';

export enum GiftCardsActions {
  setInitialSpacesState = 'setInitialSpacesState',
  setCurrentSpace = 'setCurrentSpace',
  setCurrentSpaceSection = 'setCurrentSpaceSection',
  setGiftCardSelection = 'setGiftCardSelection',
  setSelectedSpaceId = 'setSelectedSpaceId',
  setSpaceInMobileEditMode = 'setSpaceInMobileEditMode',
}

type IGiftCardsStateAction =
  | {
      type: GiftCardsActions.setInitialSpacesState;
      payload: { giftCardGroups: IGroup[]; initialItems: { [key: string]: IGroupItems[] } } | null;
    }
  | { type: GiftCardsActions.setCurrentSpace; payload: string | null }
  | {
      type: GiftCardsActions.setCurrentSpaceSection;
      payload: { spaceSection: IGiftCardSpaceSection | null; id: string };
    }
  | { type: GiftCardsActions.setGiftCardSelection; payload: boolean }
  | {
      type: GiftCardsActions.setSelectedSpaceId;
      payload: string | null;
    }
  | {
      type: GiftCardsActions.setSpaceInMobileEditMode;
      payload: { spaceId: string | null; itemId?: string };
    };

export type IGiftCardsDispatch = (action: IGiftCardsStateAction) => void;

export interface IGiftCardsState {
  giftCardSelection: boolean;
  selectedSpaceId: string;
  spaces: { [id: string]: IGiftCardSpace };
  spaceSections: { [id: string]: IGiftCardSpaceSection };
  spaceInMobileEditMode: string;
  selectedItemId: string;
}

export const giftCardsReducer = (state: IGiftCardsState, action: IGiftCardsStateAction) => {
  switch (action.type) {
    case GiftCardsActions.setInitialSpacesState:
      {
        const { giftCardGroups, initialItems } = action.payload;

        let selectedSpaceId = null;

        for (const group of giftCardGroups) {
          const items = initialItems[group.id].map((groupItem: IGroupItems, index: number) => {
            return {
              id: `${groupItem.groupId}-${index}`,
              spaceId: groupItem.groupId,
              placement: initialSpaceItemsPlacement[index],
              productTemplate: groupItem.productTemplate,
            };
          });

          const card = {
            id: group.id,
            groupId: group.id,
            items,
            background: group.backgroundsList[0],
            name: group.description,
          };

          state.spaces[group.id] = card;
          state.spaceSections[group.id] = transformSpaces(card);

          if (!selectedSpaceId) selectedSpaceId = group.id;
        }
        state.selectedSpaceId = selectedSpaceId;
      }
      break;
    case GiftCardsActions.setCurrentSpace: {
      state.selectedSpaceId = action.payload;
      state.giftCardSelection = false;
      break;
    }

    case GiftCardsActions.setCurrentSpaceSection: {
      const { spaceSection, id } = action.payload;
      state.spaceSections[id] = spaceSection;
      state.selectedItemId = 'reset';

      const sortedSubsections = spaceSection.subsections.sort(
        (a, b) => a.placement?.z - b.placement?.z,
      );
      for (const [index, subsection] of sortedSubsections.entries()) {
        if (spaceSection.itemsList) {
          state.spaces[id].items[index].productTemplate =
            spaceSection.itemsList[index].productTemplate;
        }
        const idx = state.spaces[id].items.findIndex((item) => item.id === subsection.id);
        state.spaces[id].items[idx].placement = subsection.placement;
      }

      break;
    }

    case GiftCardsActions.setGiftCardSelection: {
      state.giftCardSelection = action.payload;
      break;
    }

    case GiftCardsActions.setSelectedSpaceId: {
      state.selectedSpaceId = action.payload;
      break;
    }
    case GiftCardsActions.setSpaceInMobileEditMode: {
      state.spaceInMobileEditMode = action.payload.spaceId;
      if (action.payload.itemId) {
        state.selectedItemId = action.payload.itemId;
      }
      break;
    }

    default:
      return state;
  }
};
